/**
 * PDF 转化补点（51 只报事件名；详情进本地 props）：
 * - auth.sdk_ready / auth.click_open：登录窗就绪与点击耗时
 * - deposit.success_first / deposit.recover；trade.success_first
 * - onboarding.*：登录后盘口引导 + 试玩盘 guide 完成/跳过
 *
 * visit.engaged_action 见 analytics-visit-engaged.js（由 store 动态挂接）。
 */
import { getUserId } from '@/utils/auth-token.js';
import {
  trackAnalyticsLog,
  withAcquisitionProps,
  withDepositFunnelValue,
  withTradeFunnelValue,
} from '@/utils/analytics-track';

const AUTH_SDK_READY_STORAGE = 'deepsage-analytics-auth-sdk-ready';
const DEPOSIT_FIRST_PREFIX = 'ds-analytics-deposit-first:';
const TRADE_FIRST_PREFIX = 'ds-analytics-trade-first:';
const DEPOSIT_FAIL_AT_PREFIX = 'ds-analytics-deposit-fail-at:';
const GUIDE_OUTCOME_PREFIX = 'ds-analytics-guide-outcome:';
const RECOVER_WINDOW_MS = 24 * 60 * 60 * 1000;

let authOpenClickAt = 0;
let authClickOpenTrackedForClick = false;

function sessionFlag(key) {
  if (typeof sessionStorage === 'undefined') return true;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return true;
  }
}

function markSessionFlag(key) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
}

function userStorageKey(prefix, userId) {
  const uid = String(userId ?? getUserId() ?? '').trim();
  return uid ? `${prefix}${uid}` : `${prefix}anon`;
}

function localFlag(key) {
  if (typeof localStorage === 'undefined') return true;
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return true;
  }
}

function markLocalFlag(key) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
}

/** 用户点击打开登录/注册前调用 */
export function noteAuthOpenClick() {
  authOpenClickAt = Date.now();
  authClickOpenTrackedForClick = false;
}

/**
 * 登录窗真正打开（可交互）时调用。
 * @param {{ source?: string, contextKey?: string }} [meta]
 */
export function noteAuthDialogOpened(meta = {}) {
  if (!sessionFlag(AUTH_SDK_READY_STORAGE)) {
    markSessionFlag(AUTH_SDK_READY_STORAGE);
    trackAnalyticsLog({
      key: 'auth.sdk_ready',
      label: '登录窗就绪',
      value: withAcquisitionProps({
        source: meta.source || undefined,
        contextKey: meta.contextKey || undefined,
      }),
    });
  }

  if (!authOpenClickAt || authClickOpenTrackedForClick) return;
  authClickOpenTrackedForClick = true;
  const durationMs = Math.max(0, Date.now() - authOpenClickAt);
  authOpenClickAt = 0;
  trackAnalyticsLog({
    key: 'auth.click_open',
    label: '点击打开登录耗时',
    value: withAcquisitionProps({
      durationMs,
      source: meta.source || undefined,
      contextKey: meta.contextKey || undefined,
    }),
    durationMs,
  });
}

/**
 * @param {Record<string, unknown>} [value]
 */
export function tryTrackDepositSuccessFirst(value = {}) {
  const key = userStorageKey(DEPOSIT_FIRST_PREFIX);
  if (localFlag(key)) return;
  markLocalFlag(key);
  trackAnalyticsLog({
    key: 'deposit.success_first',
    label: '首次充值成功',
    value: withDepositFunnelValue(value),
  });
}

/** 充值失败后记时间，供 24h 内 recover */
export function noteDepositFailForRecover() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(userStorageKey(DEPOSIT_FAIL_AT_PREFIX), String(Date.now()));
  } catch {
    /* ignore */
  }
}

/**
 * @param {Record<string, unknown>} [value]
 */
export function tryTrackDepositRecover(value = {}) {
  if (typeof localStorage === 'undefined') return;
  const failKey = userStorageKey(DEPOSIT_FAIL_AT_PREFIX);
  let failAt = 0;
  try {
    failAt = Number(localStorage.getItem(failKey) || 0);
  } catch {
    return;
  }
  if (!Number.isFinite(failAt) || failAt <= 0) return;
  if (Date.now() - failAt > RECOVER_WINDOW_MS) {
    try {
      localStorage.removeItem(failKey);
    } catch {
      /* ignore */
    }
    return;
  }
  try {
    localStorage.removeItem(failKey);
  } catch {
    /* ignore */
  }
  trackAnalyticsLog({
    key: 'deposit.recover',
    label: '失败后恢复充值成功',
    value: withDepositFunnelValue({
      ...value,
      recoverWithinMs: Date.now() - failAt,
    }),
  });
}

/**
 * deposit.success 打完后补 first / recover。
 * @param {Record<string, unknown>} [value]
 */
export function afterDepositSuccessTracked(value = {}) {
  tryTrackDepositSuccessFirst(value);
  tryTrackDepositRecover(value);
  // 入账已完成：绑定 + 领奖
  scheduleCustodialBindAfterPaidDeposit({ settleRewards: true });
}

/**
 * MixPay/Cobo 等通道「已付款」后托管通道就绪。
 *
 * 2026-09-07 起主路径为服务端 EnsureCustodialTradeAddress（回调预建 type=2）；
 * FE 先轮询 trade-address/list 水合本地地址，**禁止**一上来就跑 Privy SDK create。
 * 仅当短窗内平台仍无 type=2（Ensure 失败）才回退静默 SDK 绑定。
 *
 * @param {{ settleRewards?: boolean }} [opts]
 *   settleRewards 默认 true（入账后领种子积分/体验金）；付款未入账时应 false，避免无余额空跑领奖。
 */
export function scheduleCustodialBindAfterPaidDeposit(opts = {}) {
  const settleRewards = opts.settleRewards !== false;
  return runCustodialBindAfterPaid({ settleRewards });
}

/** @type {Promise<void>|null} 仅覆盖托管水合/绑定，可被付款/入账两条路径共享 */
let custodialBindOnlyInflight = null;

/**
 * 付款/入账后：list 水合优先 → SDK 兜底；领奖与绑定解耦。
 *
 * @param {{ settleRewards?: boolean }} [opts]
 */
function runCustodialBindAfterPaid(opts = {}) {
  const settleRewards = opts.settleRewards !== false;
  return (async () => {
    if (!custodialBindOnlyInflight) {
      custodialBindOnlyInflight = hydrateOrBindCustodialAfterPaidDeposit().finally(() => {
        custodialBindOnlyInflight = null;
      });
    }
    try {
      await custodialBindOnlyInflight;
    } catch (err) {
      console.warn('[deposit] post-paid custodial hydrate/bind failed', err);
    }
    if (settleRewards) {
      await settleRewardsAfterDepositReady();
    }
  })();
}

/**
 * 服务端预建优先：拉长退避轮询 list；命中 type=2 则水合本地态。
 * 仍无地址时再 list 一次后才走 FE Privy SDK（避免与 Ensure 并行 create）。
 *
 * @returns {Promise<void>}
 */
async function hydrateOrBindCustodialAfterPaidDeposit() {
  const { useCustodialSessionStore } = await import('@/stores/custodialSession');
  const login = await import('@/utils/privy-platform-login');
  const { getWalletAddress } = await import('@/utils/auth-token');

  // 等服务端 Ensure + AddTradeAddress（Privy REST 常 2–15s；过短易与 Ensure 竞态双钱包）
  const hydrateDelays = [0, 800, 1600, 3200, 5000, 8000];
  for (let i = 0; i < hydrateDelays.length; i += 1) {
    if (hydrateDelays[i] > 0) {
      await new Promise((r) => setTimeout(r, hydrateDelays[i]));
    }
    const synced = await login.syncCustodialSessionFromTradeAddresses?.();
    if (synced) {
      useCustodialSessionStore().markReadyAfterBind();
      void softSyncPrivySessionAfterHydrate();
      return;
    }
  }

  // 兜底前最后一次 list：服务端可能刚写完
  if (await login.syncCustodialSessionFromTradeAddresses?.()) {
    useCustodialSessionStore().markReadyAfterBind();
    void softSyncPrivySessionAfterHydrate();
    return;
  }

  const last = await login.scheduleSilentPrivyBindAfterFirstDeposit?.();
  if (last?.ok || last?.skipped) {
    useCustodialSessionStore().markReadyAfterBind();
    return;
  }
  if (last?.manualAuth) {
    return;
  }
  // 若 SDK 失败但平台已绑：只水合，勿再 force create
  if (await login.syncCustodialSessionFromTradeAddresses?.()) {
    useCustodialSessionStore().markReadyAfterBind();
    void softSyncPrivySessionAfterHydrate();
    return;
  }
  await useCustodialSessionStore().ensure({ force: true, maxAttempts: 2 });
  if (getWalletAddress()) {
    void softSyncPrivySessionAfterHydrate();
  }
}

/**
 * 平台已有 type=2 后：Custom JWT 对齐 Privy 会话，**禁止 create**（expectedAddress + createIfMissing:false）。
 */
async function softSyncPrivySessionAfterHydrate() {
  try {
    const { getWalletAddress } = await import('@/utils/auth-token');
    const expectedAddress = String(getWalletAddress() ?? '').trim();
    if (!expectedAddress) return;
    const { syncPrivyAfterPlatformLogin } = await import('@/utils/privy');
    await syncPrivyAfterPlatformLogin({
      expectedAddress,
      createIfMissing: false,
    });
  } catch (err) {
    console.info('[deposit] soft Privy session sync after hydrate skipped', err?.message || err);
  }
}

/**
 * 入账后并行结算待领奖励：
 * - 种子积分：welcome-pack 域内退避静默签（仅托管）
 * - 体验金：服务端待入账在地址就绪后自动 mint；此处只刷新余额/条件
 * 二者独立；welcome-pack inFlight 防双签。
 */
async function settleRewardsAfterDepositReady() {
  await Promise.allSettled([
    claimSeedPointsAfterDepositReady(),
    retryTrialClaimAfterDepositReady(),
  ]);
}

/** 委托 welcome-pack 域；失败只打日志，不抛。 */
async function claimSeedPointsAfterDepositReady() {
  try {
    const { claimPendingWelcomePackAfterCustodialReady } = await import('@/api/welcome-pack');
    await claimPendingWelcomePackAfterCustodialReady();
  } catch (err) {
    console.warn('[deposit] post-success welcome-pack claim failed', err);
  }
}

/**
 * 入账 + Privy 就绪后：刷新体验金条件/余额（服务端自动 mint，不再二次签领）。
 */
async function retryTrialClaimAfterDepositReady() {
  try {
    const { useTrialFundStore } = await import('@/stores/trialFund');
    const store = useTrialFundStore();
    await store.retryClaimAfterDepositIfNeeded();
  } catch (err) {
    console.warn('[deposit] post-success trial balance refresh failed', err);
  }
}

/** deposit.fail 打完后记 recover 窗口 */
export function afterDepositFailTracked() {
  noteDepositFailForRecover();
}

/**
 * @param {Record<string, unknown>} [value]
 */
export function tryTrackTradeSuccessFirst(value = {}) {
  const key = userStorageKey(TRADE_FIRST_PREFIX);
  if (localFlag(key)) return;
  markLocalFlag(key);
  trackAnalyticsLog({
    key: 'trade.success_first',
    label: '首次下单成功',
    value: withTradeFunnelValue(value),
  });
}

/**
 * 登录后新手跳转种类。
 * @param {'trialSim'|'updown5m'|'home'|'soft'|'skip'} kind
 * @param {{ loginCount?: number }} [extra]
 */
export function trackPostLoginOnboarding(kind, extra = {}) {
  const map = {
    trialSim: { key: 'onboarding.trial_sim', label: '登录引导试玩盘' },
    updown5m: { key: 'onboarding.updown', label: '登录引导分钟盘' },
    home: { key: 'onboarding.home', label: '登录引导回首页' },
  };
  const hit = map[kind];
  if (!hit) return;
  trackAnalyticsLog({
    key: hit.key,
    label: hit.label,
    value: withAcquisitionProps({
      loginCount: extra.loginCount,
      kind,
    }),
  });
}

/** 新用户盘口引导开始（每用户一次） */
export function tryTrackOnboardingGuideStart() {
  const key = `${userStorageKey(GUIDE_OUTCOME_PREFIX)}:start`;
  if (localFlag(key)) return;
  markLocalFlag(key);
  trackAnalyticsLog({
    key: 'onboarding.guide_start',
    label: '盘口引导开始',
    value: withAcquisitionProps({}),
  });
}

/**
 * 盘口引导完成或跳过（每用户每种 outcome 一次）。
 * @param {'complete'|'skip'} outcome
 * @param {{ step?: string }} [extra]
 */
export function tryTrackOnboardingGuideOutcome(outcome, extra = {}) {
  const kind = outcome === 'skip' ? 'skip' : 'complete';
  const flagKey = `${userStorageKey(GUIDE_OUTCOME_PREFIX)}:${kind}`;
  if (localFlag(flagKey)) return;
  markLocalFlag(flagKey);
  // 互斥：完成与跳过只保留一种
  const other = kind === 'skip' ? 'complete' : 'skip';
  markLocalFlag(`${userStorageKey(GUIDE_OUTCOME_PREFIX)}:${other}`);
  trackAnalyticsLog({
    key: kind === 'skip' ? 'onboarding.guide_skip' : 'onboarding.guide_complete',
    label: kind === 'skip' ? '盘口引导跳过' : '盘口引导完成',
    value: withAcquisitionProps({
      outcome: kind,
      step: extra.step || undefined,
    }),
  });
}
